import React, { createContext, useContext } from 'react';
import { Edge } from '@xyflow/react';

import { PipelineEditorProps } from '../../PipelineEditor.types';
import { PipelineNodeV2 } from '../types/BackendPipeline.types';
import { ReactFlowNodeV2 } from '../types/ReactFlow.types';

/**
 * Ref-container for PipelineCanvas's useNodesState/useEdgesState setters.
 * The canvas is rendered BELOW PipelineEditorContext.Provider, so the provider
 * can't own the setters directly. Instead the provider ships a mutable ref
 * that the canvas fills in on mount; anyone outside the canvas tree (e.g. the
 * SyncAI chat panel in usePipelineActions) can then call the real setters that
 * actually feed the rendered `nodes` prop. React Flow's own useReactFlow()
 * setNodes / addNodes do NOT propagate to useNodesState in controlled mode,
 * which is why we need this hatch.
 */
export interface CanvasStateSetters {
  setNodes?: React.Dispatch<React.SetStateAction<ReactFlowNodeV2[]>>;
  setEdges?: React.Dispatch<React.SetStateAction<Edge[]>>;
}

export interface PipelineEditorContextType extends PipelineEditorProps {
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: React.Dispatch<React.SetStateAction<boolean>>;
  selectedGraphNode: ReactFlowNodeV2 | null;
  saveNodeConfiguration: (node: Partial<PipelineNodeV2>) => void;
  savePipeline: () => Promise<void>;
  supplementalNodeData: Record<string, any>;
  isDraft: boolean;
  isPipelineReadOnly: boolean;
  setSelectedNodeIds: React.Dispatch<React.SetStateAction<string[]>>;
  showGroupingModal: boolean;
  setShowGroupingModal: React.Dispatch<React.SetStateAction<boolean>>;
  showNodeSelector: boolean;
  setShowNodeSelector: React.Dispatch<React.SetStateAction<boolean>>;
  showRightPanel: boolean;
  setShowRightPanel: React.Dispatch<React.SetStateAction<boolean>>;
  isNodeDragging: boolean;
  setIsNodeDragging: React.Dispatch<React.SetStateAction<boolean>>;
  pendingDeleteGroupIds: string[];
  setPendingDeleteGroupIds: React.Dispatch<React.SetStateAction<string[]>>;
  canvasSetters: React.MutableRefObject<CanvasStateSetters>;
}

export const PipelineEditorContext = createContext<PipelineEditorContextType>({} as any);

export const usePipelineEditor = () => useContext(PipelineEditorContext);
